import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { Target, TrendingUp, CheckCircle, BarChart3, Activity, BookOpen } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';

const HabitsModule = () => {
  const { t } = useTranslation();
  const [activeView, setActiveView] = useState('overview'); // 'overview', 'diagnostic', 'dashboard', 'library', 'checkin'
  const [diagnosticCompleted, setDiagnosticCompleted] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load initial data
  useEffect(() => {
    const loadHabitData = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/habits/dashboard`);
        if (response.data.has_data) {
          setDiagnosticCompleted(true);
          setDashboardData(response.data);
        }
      } catch (error) {
        console.error('Error loading habit data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadHabitData();
  }, []);

  if (loading) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <p className="text-slate-600">{t('habits.ui.loading')}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {activeView === 'overview' && (
        <HabitsOverview 
          diagnosticCompleted={diagnosticCompleted}
          dashboardData={dashboardData}
          onStartDiagnostic={() => setActiveView('diagnostic')}
          onViewDashboard={() => setActiveView('dashboard')}
          onViewLibrary={() => setActiveView('library')}
          onViewCheckIn={() => setActiveView('checkin')}
        />
      )}

      {activeView === 'diagnostic' && (
        <HabitsDiagnostic 
          onComplete={async (submissionData) => {
            setDiagnosticCompleted(true);
            // Fetch full dashboard data after submission
            try {
              const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/habits/dashboard`);
              if (response.data.has_data) {
                setDashboardData(response.data);
                setActiveView('dashboard');
              }
            } catch (error) {
              console.error('Error loading dashboard data:', error);
              setDashboardData(submissionData);
              setActiveView('dashboard');
            }
          }}
          onBack={() => setActiveView('overview')}
        />
      )}

      {activeView === 'dashboard' && (
        <HabitsDashboard 
          data={dashboardData}
          onBack={() => setActiveView('overview')}
          onViewLibrary={() => setActiveView('library')}
        />
      )}

      {activeView === 'library' && (
        <HabitsLibrary 
          onBack={() => setActiveView('overview')}
          weakDomains={dashboardData?.weak_domains || []}
        />
      )}

      {activeView === 'checkin' && (
        <HabitsCheckIn 
          onBack={() => setActiveView('overview')}
        />
      )}
    </div>
  );
};

// Overview Component
const HabitsOverview = ({ diagnosticCompleted, dashboardData, onStartDiagnostic, onViewDashboard, onViewLibrary, onViewCheckIn }) => {
  const { t } = useTranslation();

  return (
    <div className="space-y-6">
      {/* Hero Card */}
      <Card className="bg-gradient-to-r from-teal-500 to-cyan-600 text-white">
        <CardContent className="p-8">
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
              <Activity className="w-8 h-8" />
            </div>
            <div className="flex-1">
              <h2 className="text-3xl font-bold mb-2">{t('habits.title')}</h2>
              <p className="text-lg opacity-90">{t('habits.description')}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Action Cards */}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Diagnostic Card */}
        <Card className="border-2 border-teal-200 hover:border-teal-400 transition-colors">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-teal-600" />
              {t('habits.diagnostic.title')}
            </CardTitle>
            <CardDescription>{t('habits.diagnostic.description')}</CardDescription>
          </CardHeader>
          <CardContent>
            {diagnosticCompleted ? (
              <div className="space-y-3">
                <Badge className="bg-green-100 text-green-800">{t('habits.ui.completed')}</Badge>
                <Button 
                  onClick={onStartDiagnostic}
                  variant="outline"
                  className="w-full"
                >
                  {t('habits.diagnostic.retake')}
                </Button>
              </div>
            ) : (
              <Button 
                onClick={onStartDiagnostic}
                className="w-full bg-teal-600 hover:bg-teal-700"
              >
                {t('habits.diagnostic.start')}
              </Button>
            )}
          </CardContent>
        </Card>

        {/* Dashboard Card */}
        <Card className="border-2 border-cyan-200 hover:border-cyan-400 transition-colors">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-cyan-600" />
              {t('habits.dashboard.title')}
            </CardTitle>
            <CardDescription>{t('habits.dashboard.description')}</CardDescription>
          </CardHeader>
          <CardContent>
            {diagnosticCompleted ? (
              <Button 
                onClick={onViewDashboard}
                className="w-full bg-cyan-600 hover:bg-cyan-700"
              >
                {t('habits.results.viewDashboard')}
              </Button>
            ) : (
              <p className="text-sm text-slate-500">{t('habits.dashboard.completeDiagnostic')}</p>
            )}
          </CardContent>
        </Card>

        {/* Library Card */}
        <Card className="border-2 border-emerald-200 hover:border-emerald-400 transition-colors">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-emerald-600" />
              {t('habits.library.title')}
            </CardTitle>
            <CardDescription>{t('habits.library.description')}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button 
              onClick={onViewLibrary}
              className="w-full bg-emerald-600 hover:bg-emerald-700"
            >
              {t('habits.library.browse')}
            </Button>
          </CardContent>
        </Card>

        {/* Check-In Card */}
        <Card className="border-2 border-blue-200 hover:border-blue-400 transition-colors">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-blue-600" />
              {t('habits.checkin.title')}
            </CardTitle>
            <CardDescription>{t('habits.checkin.description')}</CardDescription>
          </CardHeader>
          <CardContent>
            {diagnosticCompleted ? (
              <Button 
                onClick={onViewCheckIn}
                className="w-full bg-blue-600 hover:bg-blue-700"
              >
                {t('habits.checkin.start')}
              </Button>
            ) : (
              <p className="text-sm text-slate-500">{t('habits.checkin.needsDiagnostic')}</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Habits Diagnostic Component (28 questions across 7 domains)
const HabitsDiagnostic = ({ onComplete, onBack }) => {
  const { t } = useTranslation();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [responses, setResponses] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Define all 28 questions (4 per domain × 7 domains)
  const questions = [
    // Financial Discipline (4 questions)
    { id: 'financial_discipline_1', domain: 'financial_discipline', text: t('habits.diagnostic.questions.financial_discipline_1') },
    { id: 'financial_discipline_2', domain: 'financial_discipline', text: t('habits.diagnostic.questions.financial_discipline_2') },
    { id: 'financial_discipline_3', domain: 'financial_discipline', text: t('habits.diagnostic.questions.financial_discipline_3') },
    { id: 'financial_discipline_4', domain: 'financial_discipline', text: t('habits.diagnostic.questions.financial_discipline_4') },
    // Goal Alignment (4 questions)
    { id: 'goal_alignment_1', domain: 'goal_alignment', text: t('habits.diagnostic.questions.goal_alignment_1') },
    { id: 'goal_alignment_2', domain: 'goal_alignment', text: t('habits.diagnostic.questions.goal_alignment_2') },
    { id: 'goal_alignment_3', domain: 'goal_alignment', text: t('habits.diagnostic.questions.goal_alignment_3') },
    { id: 'goal_alignment_4', domain: 'goal_alignment', text: t('habits.diagnostic.questions.goal_alignment_4') },
    // Wealth Building (4 questions)
    { id: 'wealth_building_1', domain: 'wealth_building', text: t('habits.diagnostic.questions.wealth_building_1') },
    { id: 'wealth_building_2', domain: 'wealth_building', text: t('habits.diagnostic.questions.wealth_building_2') },
    { id: 'wealth_building_3', domain: 'wealth_building', text: t('habits.diagnostic.questions.wealth_building_3') },
    { id: 'wealth_building_4', domain: 'wealth_building', text: t('habits.diagnostic.questions.wealth_building_4') },
    // Self-Management (4 questions)
    { id: 'self_management_1', domain: 'self_management', text: t('habits.diagnostic.questions.self_management_1') },
    { id: 'self_management_2', domain: 'self_management', text: t('habits.diagnostic.questions.self_management_2') },
    { id: 'self_management_3', domain: 'self_management', text: t('habits.diagnostic.questions.self_management_3') },
    { id: 'self_management_4', domain: 'self_management', text: t('habits.diagnostic.questions.self_management_4') },
    // Emotional Regulation (4 questions)
    { id: 'emotional_regulation_1', domain: 'emotional_regulation', text: t('habits.diagnostic.questions.emotional_regulation_1') },
    { id: 'emotional_regulation_2', domain: 'emotional_regulation', text: t('habits.diagnostic.questions.emotional_regulation_2') },
    { id: 'emotional_regulation_3', domain: 'emotional_regulation', text: t('habits.diagnostic.questions.emotional_regulation_3') },
    { id: 'emotional_regulation_4', domain: 'emotional_regulation', text: t('habits.diagnostic.questions.emotional_regulation_4') },
    // Continuous Learning (4 questions)
    { id: 'continuous_learning_1', domain: 'continuous_learning', text: t('habits.diagnostic.questions.continuous_learning_1') },
    { id: 'continuous_learning_2', domain: 'continuous_learning', text: t('habits.diagnostic.questions.continuous_learning_2') },
    { id: 'continuous_learning_3', domain: 'continuous_learning', text: t('habits.diagnostic.questions.continuous_learning_3') },
    { id: 'continuous_learning_4', domain: 'continuous_learning', text: t('habits.diagnostic.questions.continuous_learning_4') },
    // Health & Energy (4 questions)
    { id: 'health_1', domain: 'health', text: t('habits.diagnostic.questions.health_1') },
    { id: 'health_2', domain: 'health', text: t('habits.diagnostic.questions.health_2') },
    { id: 'health_3', domain: 'health', text: t('habits.diagnostic.questions.health_3') },
    { id: 'health_4', domain: 'health', text: t('habits.diagnostic.questions.health_4') },
  ];

  const currentQ = questions[currentQuestion];
  const progress = ((currentQuestion + 1) / questions.length) * 100;

  const handleAnswer = (rating) => {
    setResponses({ ...responses, [currentQ.id]: rating });
  };

  const handleNext = () => {
    if (responses[currentQ.id]) {
      if (currentQuestion < questions.length - 1) {
        setCurrentQuestion(currentQuestion + 1);
      } else {
        handleSubmit();
      }
    } else {
      toast.error('Please select an answer');
    }
  };

  const handleBack = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        responses: Object.entries(responses).map(([question_id, rating]) => ({
          question_id,
          rating: parseInt(rating)
        })),
        language: 'en'
      };

      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/habits/diagnostic/submit`,
        payload
      );

      toast.success('Diagnostic completed successfully!');
      onComplete(response.data);
    } catch (error) {
      console.error('Error submitting diagnostic:', error);
      toast.error('Failed to submit diagnostic. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 to-cyan-50 p-4">
      <div className="max-w-2xl mx-auto pt-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h1 className="text-2xl font-bold text-black mb-2">{t('habits.diagnostic.title')}</h1>
              <p className="text-slate-600">
                {t('habits.diagnostic.questionOf', { current: currentQuestion + 1, total: questions.length })}
              </p>
            </div>
            <Button variant="outline" onClick={onBack}>
              {t('habits.ui.exit')}
            </Button>
          </div>

          {/* Progress Bar */}
          <div className="w-full bg-gray-200 rounded-full h-4">
            <div 
              className="bg-gradient-to-r from-teal-500 to-cyan-600 h-4 rounded-full transition-all duration-500 flex items-center justify-end pr-2"
              style={{ width: `${progress}%` }}
            >
              <span className="text-white text-xs font-medium">{Math.round(progress)}%</span>
            </div>
          </div>
        </div>

        {/* Question Card */}
        <Card className="shadow-lg border-0 backdrop-blur-sm bg-white/90">
          <CardHeader>
            <Badge variant="outline" className="mb-4 w-fit">
              {t(`habits.diagnostic.domains.${currentQ.domain}`)}
            </Badge>
            <CardTitle className="text-xl text-slate-800 leading-relaxed">
              {currentQ.text}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Likert Scale Options (1-5) */}
            <div className="space-y-3">
              {[1, 2, 3, 4, 5].map((rating) => (
                <label
                  key={rating}
                  className={`flex items-center p-4 rounded-lg border-2 cursor-pointer transition-all hover:bg-teal-50 ${
                    responses[currentQ.id] === rating
                      ? 'border-teal-500 bg-teal-50'
                      : 'border-slate-200'
                  }`}
                >
                  <input
                    type="radio"
                    name={`question-${currentQ.id}`}
                    value={rating}
                    checked={responses[currentQ.id] === rating}
                    onChange={(e) => handleAnswer(parseInt(e.target.value))}
                    className="w-4 h-4 text-teal-600 mr-3"
                  />
                  <span className="text-slate-700">{t(`habits.diagnostic.scale.${rating}`)}</span>
                </label>
              ))}
            </div>

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-6">
              <Button
                onClick={handleBack}
                variant="outline"
                disabled={currentQuestion === 0}
              >
                {t('habits.ui.previous')}
              </Button>
              <Button
                onClick={handleNext}
                disabled={!responses[currentQ.id] || submitting}
                className="bg-teal-600 hover:bg-teal-700"
              >
                {currentQuestion === questions.length - 1 
                  ? (submitting ? t('habits.diagnostic.submitting') : t('habits.diagnostic.submit'))
                  : t('habits.ui.next')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Habits Dashboard Component (identical graph to Beliefs)
const HabitsDashboard = ({ data, onBack, onViewLibrary }) => {
  const { t } = useTranslation();

  if (!data || !data.has_data) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <p className="text-slate-600 mb-4">{t('habits.dashboard.noData')}</p>
          <Button onClick={onBack}>{t('habits.ui.goBack')}</Button>
        </CardContent>
      </Card>
    );
  }

  // Prepare bar chart data - same format as Beliefs
  const chartData = Object.entries(data.domain_scores || {}).map(([domain, score]) => {
    const barColor = '#6b21a8'; // Dark purple (same as Beliefs)
    
    return {
      domain: t(`habits.diagnostic.domains.${domain}`),
      score: score,
      originalScore: score,
      color: barColor
    };
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-start">
            <div>
              <CardTitle className="text-2xl">{t('habits.dashboard.title')}</CardTitle>
              <CardDescription>{t('habits.dashboard.description')}</CardDescription>
            </div>
            <Button variant="outline" onClick={onBack}>
              {t('habits.ui.goBack')}
            </Button>
          </div>
        </CardHeader>
      </Card>

      {/* Bar Chart - Identical to Beliefs Module */}
      <Card>
        <CardContent className="pt-6 pl-2 pr-4 md:pl-4 md:pr-8 lg:pl-6 lg:pr-12">
          {/* Legend - Responsive */}
          <div className="mb-6 p-4 bg-slate-50 rounded-lg">
            <div className="flex flex-col md:flex-row items-center justify-center gap-3 md:gap-6">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded flex-shrink-0" style={{ background: 'linear-gradient(to right, #b91c1c, #dc2626)' }}></div>
                <span className="text-slate-700 font-medium text-xs md:text-sm">{t('habits.ui.weakLabel')} ({t('habits.ui.weakRange')})</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded flex-shrink-0" style={{ background: 'linear-gradient(to right, #f97316, #fbbf24)' }}></div>
                <span className="text-slate-700 font-medium text-xs md:text-sm">{t('habits.ui.developingLabel')} ({t('habits.ui.developingRange')})</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded flex-shrink-0" style={{ background: 'linear-gradient(to right, #86efac, #16a34a)' }}></div>
                <span className="text-slate-700 font-medium text-xs md:text-sm">{t('habits.ui.strongLabel')} ({t('habits.ui.strongRange')})</span>
              </div>
            </div>
            <div className="mt-3 text-center">
              <span className="text-xs text-slate-600">Purple bars show your habit consistency on a gradient background</span>
            </div>
          </div>

          {/* Chart Title */}
          <div className="flex items-center mb-4" style={{ paddingLeft: '140px', paddingRight: '30px' }}>
            <div className="flex-1 text-center">
              <h3 className="text-base md:text-xl font-bold text-slate-700">
                {t('habits.ui.chartAxisLeft')} | {t('habits.ui.chartAxisRight')}
              </h3>
            </div>
          </div>

          {/* Scale Labels Above Chart - Aligned with chart area */}
          <div className="flex items-center mb-2" style={{ paddingLeft: '140px' }}>
            <div className="flex justify-between items-center flex-1" style={{ paddingRight: '30px' }}>
              <span className="text-xs font-medium text-slate-600">{t('habits.ui.scaleNever')}</span>
              <span className="text-xs font-medium text-slate-600">{t('habits.ui.scaleRarely')}</span>
              <span className="text-xs font-medium text-slate-600">{t('habits.ui.scaleSometimes')}</span>
              <span className="text-xs font-medium text-slate-600">{t('habits.ui.scaleOften')}</span>
              <span className="text-xs font-medium text-slate-600">{t('habits.ui.scaleAlways')}</span>
            </div>
          </div>

          {/* Bar Chart - Responsive */}
          <div className="w-full overflow-x-auto">
            <div className="min-w-[320px]">
              <ResponsiveContainer width="100%" height={350}>
                <BarChart 
                  data={chartData} 
                  layout="vertical"
                  margin={{ top: 5, right: 15, left: 15, bottom: 5 }}
                >
                  <defs>
                    <linearGradient id="habitGradient" x1="0" y1="0" x2="1" y2="0">
                      <stop offset="0%" stopColor="#b91c1c" stopOpacity={0.4} />
                      <stop offset="30%" stopColor="#dc2626" stopOpacity={0.35} />
                      <stop offset="50%" stopColor="#fbbf24" stopOpacity={0.3} />
                      <stop offset="70%" stopColor="#86efac" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="#16a34a" stopOpacity={0.4} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" fill="url(#habitGradient)" vertical={false} />
                  <XAxis 
                    type="number" 
                    domain={[0, 5]}
                    ticks={[1, 2, 3, 4, 5]}
                    tick={{ fontSize: 10 }}
                  />
                  <YAxis 
                    type="category" 
                    dataKey="domain" 
                    width={110}
                    tick={{ fontSize: 11 }}
                  />
                  <Tooltip 
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        const data = payload[0].payload;
                        return (
                          <div className="bg-white p-3 border border-slate-200 rounded shadow-lg">
                            <p className="font-semibold text-slate-800">{data.domain}</p>
                            <p className="text-sm text-slate-600">
                              {t('habits.ui.scoreLabel')}: {data.originalScore.toFixed(2)} / 5.0
                            </p>
                            <p className="text-xs text-slate-500 mt-1">
                              {data.originalScore < 3 ? t('habits.ui.weakDomain') : 
                               data.originalScore <= 4 ? t('habits.ui.developingDomain') : t('habits.ui.strongDomain')}
                            </p>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Bar dataKey="score" barSize={25}>
                    {chartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Score Details */}
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-3">
            {chartData.map((item, idx) => (
              <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <span className="text-sm font-medium text-slate-700">{item.domain}</span>
                <div className="flex items-center gap-2">
                  <span className="text-lg font-bold" style={{ color: item.color }}>
                    {item.originalScore.toFixed(1)}
                  </span>
                  <span className="text-xs text-slate-500">/ 5.0</span>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Weak and Strong Domains */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Weak Domains */}
        <Card className="border-amber-200 bg-amber-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-amber-800">
              <Target className="w-5 h-5" />
              {t('habits.dashboard.weakDomains')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data.weak_domains && data.weak_domains.length > 0 ? (
              <ul className="space-y-2">
                {data.weak_domains.map((domain, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <Target className="w-4 h-4 text-amber-600 mt-1 flex-shrink-0" />
                    <span className="text-sm text-amber-900">{t(`habits.diagnostic.domains.${domain}`)}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-amber-700">{t('habits.ui.noWeakDomains')}</p>
            )}
          </CardContent>
        </Card>

        {/* Strong Domains */}
        <Card className="border-green-200 bg-green-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-green-800">
              <CheckCircle className="w-5 h-5" />
              {t('habits.dashboard.strongDomains')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data.strong_domains && data.strong_domains.length > 0 ? (
              <ul className="space-y-2">
                {data.strong_domains.map((domain, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600 mt-1 flex-shrink-0" />
                    <span className="text-sm text-green-900">{t(`habits.diagnostic.domains.${domain}`)}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-green-700">{t('habits.ui.noStrongDomains')}</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* CTA */}
      {data.weak_domains && data.weak_domains.length > 0 && (
        <Card className="bg-gradient-to-r from-teal-500 to-cyan-600 text-white">
          <CardContent className="p-6 text-center">
            <h3 className="text-xl font-bold mb-2">{t('habits.ui.readyToImprove')}</h3>
            <p className="mb-4 opacity-90">{t('habits.ui.buildWealth')}</p>
            <Button 
              onClick={onViewLibrary}
              className="bg-white text-teal-600 hover:bg-gray-100"
            >
              {t('habits.library.browse')}
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Habits Library Component
const HabitsLibrary = ({ onBack, weakDomains }) => {
  const { t } = useTranslation();
  const [library, setLibrary] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadLibrary = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/habits/library`);
        setLibrary(response.data.library);
      } catch (error) {
        console.error('Error loading habits library:', error);
        toast.error('Failed to load habits library');
      } finally {
        setLoading(false);
      }
    };

    loadLibrary();
  }, []);

  const handleAddHabit = async (habit, domain) => {
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/habits/add`, {
        name: habit.name,
        domain: domain,
        frequency: 'daily'
      });
      toast.success('Habit added successfully!');
    } catch (error) {
      console.error('Error adding habit:', error);
      toast.error('Failed to add habit');
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <p className="text-slate-600">{t('habits.ui.loading')}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-start">
            <div>
              <CardTitle className="text-2xl">{t('habits.library.title')}</CardTitle>
              <CardDescription>{t('habits.library.subtitle')}</CardDescription>
            </div>
            <Button variant="outline" onClick={onBack}>
              {t('habits.ui.goBack')}
            </Button>
          </div>
        </CardHeader>
      </Card>

      {/* Priority Focus - Weak Domains */}
      {weakDomains && weakDomains.length > 0 && (
        <Card className="border-amber-300 bg-amber-50">
          <CardHeader>
            <CardTitle className="text-amber-900">{t('habits.library.priorityFocus')}</CardTitle>
            <CardDescription className="text-amber-700">
              {t('habits.library.priorityDescription')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {weakDomains.map((domain, idx) => (
                <Badge key={idx} className="bg-amber-200 text-amber-900">
                  {t(`habits.diagnostic.domains.${domain}`)}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Habits by Domain */}
      <div className="grid gap-6">
        {Object.entries(library).map(([domain, habits]) => (
          <Card key={domain} className={weakDomains.includes(domain) ? 'border-2 border-amber-400' : ''}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="w-5 h-5 text-teal-600" />
                {t(`habits.diagnostic.domains.${domain}`)}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-4">
                {habits.map((habit, idx) => (
                  <div key={idx} className="p-4 border border-slate-200 rounded-lg hover:border-teal-300 transition-colors">
                    <h4 className="font-semibold text-slate-800 mb-2">{habit.name}</h4>
                    <p className="text-sm text-slate-600 mb-3">{habit.description}</p>
                    <Button
                      size="sm"
                      onClick={() => handleAddHabit(habit, domain)}
                      className="bg-teal-600 hover:bg-teal-700"
                    >
                      {t('habits.library.addHabit')}
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

// Weekly Check-In Component
const HabitsCheckIn = ({ onBack }) => {
  const { t } = useTranslation();
  const [activeHabits, setActiveHabits] = useState([]);
  const [checkIns, setCheckIns] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const loadActiveHabits = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/habits/active`);
        setActiveHabits(response.data.habits);
      } catch (error) {
        console.error('Error loading active habits:', error);
        toast.error('Failed to load active habits');
      } finally {
        setLoading(false);
      }
    };

    loadActiveHabits();
  }, []);

  const handleSubmit = async () => {
    if (Object.keys(checkIns).length === 0) {
      toast.error('Please rate at least one habit');
      return;
    }

    setSubmitting(true);
    try {
      const checkInData = {
        check_ins: Object.entries(checkIns).map(([habit_id, data]) => ({
          habit_id,
          score: data.score,
          reflection: data.reflection
        })),
        period_start: new Date().toISOString().split('T')[0]
      };

      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/habits/checkin`, checkInData);
      toast.success('Weekly check-in completed!');
      onBack();
    } catch (error) {
      console.error('Error submitting check-in:', error);
      toast.error('Failed to submit check-in');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <p className="text-slate-600">{t('habits.ui.loading')}</p>
        </CardContent>
      </Card>
    );
  }

  if (activeHabits.length === 0) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <p className="text-slate-600 mb-4">{t('habits.checkin.noActiveHabits')}</p>
          <Button onClick={onBack}>{t('habits.ui.goBack')}</Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-start">
            <div>
              <CardTitle className="text-2xl">{t('habits.checkin.title')}</CardTitle>
              <CardDescription>{t('habits.checkin.weeklyPrompt')}</CardDescription>
            </div>
            <Button variant="outline" onClick={onBack}>
              {t('habits.ui.goBack')}
            </Button>
          </div>
        </CardHeader>
      </Card>

      {/* Check-In Form */}
      <div className="space-y-4">
        {activeHabits.map((habit) => (
          <Card key={habit.id}>
            <CardHeader>
              <CardTitle className="text-lg">{habit.name}</CardTitle>
              <CardDescription>{t(`habits.diagnostic.domains.${habit.domain}`)}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Rating */}
              <div>
                <label className="text-sm font-medium text-slate-700 mb-2 block">
                  {t('habits.checkin.rateConsistency')}
                </label>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map((rating) => (
                    <button
                      key={rating}
                      onClick={() => setCheckIns({
                        ...checkIns,
                        [habit.id]: { ...checkIns[habit.id], score: rating }
                      })}
                      className={`flex-1 p-3 rounded-lg border-2 transition-all ${
                        checkIns[habit.id]?.score === rating
                          ? 'border-teal-500 bg-teal-50'
                          : 'border-slate-200 hover:border-teal-300'
                      }`}
                    >
                      <div className="text-lg font-bold">{rating}</div>
                      <div className="text-xs text-slate-600">{t(`habits.diagnostic.scale.${rating}`)}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Optional Reflection */}
              <div>
                <label className="text-sm font-medium text-slate-700 mb-2 block">
                  {t('habits.checkin.reflection')} ({t('habits.ui.optional')})
                </label>
                <Textarea
                  placeholder={t('habits.checkin.reflectionPlaceholder')}
                  value={checkIns[habit.id]?.reflection || ''}
                  onChange={(e) => setCheckIns({
                    ...checkIns,
                    [habit.id]: { ...checkIns[habit.id], reflection: e.target.value }
                  })}
                  className="min-h-[80px]"
                />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Submit Button */}
      <div className="flex justify-end gap-4">
        <Button variant="outline" onClick={onBack}>
          {t('habits.ui.cancel')}
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={submitting || Object.keys(checkIns).length === 0}
          className="bg-teal-600 hover:bg-teal-700"
        >
          {submitting ? t('habits.ui.saving') : t('habits.checkin.submitCheckIn')}
        </Button>
      </div>
    </div>
  );
};

export default HabitsModule;
